import {
  Bell,
  Bold,
  CircleCheck,
  Code2,
  Eye,
  GraduationCap,
  ImagePlus,
  Info,
  Link2,
  List,
  Megaphone,
  RefreshCw,
  Search,
  Send,
  Trash2,
  TriangleAlert,
  UserCheck,
  Users as UsersIcon,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api } from "../api";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Panel,
  SectionHeader,
  Tag,
} from "../components/design-system";
import { useResource } from "../hooks/use-resource";
import { renderMarkdown, wrapMarkdownCodeBlock } from "../markdown";
import type {
  CreatePublicationInput,
  PublicationDetail,
  PublicationIconTone,
  PublicationKind,
  PublicationMedia,
  PublicationSummary,
  PublicationTargetScope,
  UserMajor,
  UserSummary,
} from "../types";
import { formatDateTime } from "../utils";

type PublicationListMode = "current" | "deleted";

const FIXED_ACCENT = "#C15F3C";
const ICONS: Array<{
  value: PublicationIconTone;
  icon: LucideIcon;
  label: string;
}> = [
  { value: "default", icon: Megaphone, label: "默认" },
  { value: "warning", icon: TriangleAlert, label: "警告" },
  { value: "success", icon: CircleCheck, label: "成功" },
  { value: "info", icon: Info, label: "信息" },
];

function iconOf(tone: PublicationIconTone, kind: PublicationKind) {
  if (tone === "default" && kind === "notification") return Bell;
  return ICONS.find((item) => item.value === tone)?.icon || Megaphone;
}

function publicationTypeLabel(publication: Pick<PublicationSummary, "kind">) {
  return publication.kind === "notification" ? "通知" : "公告";
}

function publicationAudienceLabel(publication: PublicationSummary) {
  if (publication.targetScope === "all") return "全体用户";
  if (publication.targetScope === "major") {
    const groups = publication.targetMajorGroups || [];
    const names = groups.length
      ? groups.map((group) => group.label)
      : publication.targetMajorNames;
    const unit = groups.length ? "个年级专业" : "个专业";
    const majorLabel =
      names.length > 2
        ? `${names[0]}等 ${names.length} ${unit}`
        : names.join("、") || "指定年级专业";
    return `${majorLabel} · ${publication.recipientCount} 人`;
  }
  return `${publication.recipientCount} 位指定用户`;
}

function cohortMajorKey(group: Pick<UserMajor, "cohortYear" | "majorName">) {
  return `${group.cohortYear}:${group.majorName}`;
}

interface SlidingPillOption<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
}

function SlidingPillSelector<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: readonly SlidingPillOption<T>[];
  onChange: (value: T) => void;
  label: string;
}) {
  const activeIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  return (
    <div
      className="relative grid w-full overflow-hidden rounded-full border border-sand-light bg-cream/70 p-1"
      style={{
        gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
      }}
      role="radiogroup"
      aria-label={label}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-1 left-1 top-1 rounded-full bg-ink shadow-sm transition-transform duration-200 will-change-transform motion-reduce:transition-none"
        style={{
          width: `calc((100% - 0.5rem) / ${options.length})`,
          transform: `translate3d(${activeIndex * 100}%, 0, 0)`,
          transitionTimingFunction: "cubic-bezier(0.77, 0, 0.175, 1)",
        }}
      />
      {options.map((option) => {
        const Icon = option.icon;
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={`relative z-10 flex min-w-0 items-center justify-center gap-1.5 rounded-full px-3 py-2 text-[12.5px] font-medium transition-[color,transform] duration-200 ease-out active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none ${
              selected ? "text-paper" : "text-ink-soft hover:text-ink"
            }`}
          >
            {Icon && <Icon size={14} className="shrink-0" />}
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function PublicationsPage() {
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [kind, setKind] = useState<PublicationKind>("announcement");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tone, setTone] = useState<PublicationIconTone>("default");
  const [startsAt, setStartsAt] = useState(() => localDateTime(new Date()));
  const [visibility, setVisibility] = useState<"permanent" | "timed">(
    "permanent",
  );
  const [expiresAt, setExpiresAt] = useState(() =>
    localDateTime(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
  );
  const [reminder, setReminder] = useState<"once" | "inbox" | "always">("once");
  const [targetScope, setTargetScope] = useState<PublicationTargetScope>("all");
  const [selectedUsers, setSelectedUsers] = useState<UserSummary[]>([]);
  const [targetMajorGroups, setTargetMajorGroups] = useState<UserMajor[]>([]);
  const [majorQuery, setMajorQuery] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState<UserSummary[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [media, setMedia] = useState<PublicationMedia[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<PublicationListMode>("current");
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [selected, setSelected] = useState<PublicationSummary | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const isAnnouncement = kind === "announcement";

  const publications = useResource(
    () => api.publications({ page, pageSize: 8, q: query, status: mode }),
    [page, query, mode],
  );
  const majors = useResource(() => api.userMajors(), []);
  const selectedMajorUserCount = useMemo(
    () =>
      targetMajorGroups.reduce((total, major) => total + major.userCount, 0),
    [targetMajorGroups],
  );
  const filteredMajors = useMemo(() => {
    const normalizedQuery = majorQuery.trim().toLocaleLowerCase();
    return (majors.data || []).filter((major) =>
      `${major.label} ${major.cohortYear} ${major.majorName}`
        .toLocaleLowerCase()
        .includes(normalizedQuery),
    );
  }, [majorQuery, majors.data]);
  const previewAudience =
    targetScope === "all"
      ? "发送至全体用户"
      : targetScope === "major"
        ? targetMajorGroups.length
          ? `发送至 ${targetMajorGroups.length} 个年级专业 · ${selectedMajorUserCount} 人`
          : "请至少选择一个目标年级专业"
        : `发送至 ${selectedUsers.length} 位指定用户`;

  useEffect(() => {
    const normalizedUserQuery = userQuery.trim();
    if (targetScope !== "selected" || !normalizedUserQuery) {
      setUserResults([]);
      setUsersLoading(false);
      return undefined;
    }

    let active = true;
    setUserResults([]);
    setUsersLoading(true);
    const timer = window.setTimeout(() => {
      void api
        .users({ page: 1, pageSize: 30, q: normalizedUserQuery })
        .then((result) => {
          if (active) setUserResults(result.items);
        })
        .catch((error) => {
          if (active) {
            toast.error(
              error instanceof Error ? error.message : "用户读取失败",
            );
          }
        })
        .finally(() => {
          if (active) setUsersLoading(false);
        });
    }, 220);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [targetScope, userQuery]);

  const previewMedia = useMemo(
    () => media.filter((item) => content.includes(`media://${item.id}`)),
    [content, media],
  );

  const insert = (snippet: string) => {
    const textarea = bodyRef.current;
    const start = textarea?.selectionStart ?? content.length;
    const end = textarea?.selectionEnd ?? content.length;
    setContent(
      (current) =>
        `${current.slice(0, Math.min(start, current.length))}${snippet}${current.slice(Math.min(end, current.length))}`,
    );
    window.setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(
        start + snippet.length,
        start + snippet.length,
      );
    });
  };

  const wrapSelection = (marker: string, placeholder: string) => {
    const textarea = bodyRef.current;
    const source = textarea?.value ?? content;
    const start = Math.min(
      textarea?.selectionStart ?? source.length,
      source.length,
    );
    const end = Math.min(
      textarea?.selectionEnd ?? source.length,
      source.length,
    );
    const selected = source.slice(start, end);
    const inner = selected || placeholder;
    const replacement = `${marker}${inner}${marker}`;
    setContent(`${source.slice(0, start)}${replacement}${source.slice(end)}`);
    window.setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(
        start + marker.length,
        start + marker.length + inner.length,
      );
    });
  };

  const wrapCodeBlock = () => {
    const textarea = bodyRef.current;
    const source = textarea?.value ?? content;
    const edit = wrapMarkdownCodeBlock(
      source,
      textarea?.selectionStart ?? source.length,
      textarea?.selectionEnd ?? source.length,
    );
    setContent(edit.value);
    window.setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(edit.selectionStart, edit.selectionEnd);
    });
  };

  const uploadFiles = async (files: File[]) => {
    if (!isAnnouncement || !files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} 不是支持的图片`);
          continue;
        }
        if (!file.size || file.size > 8 * 1024 * 1024) {
          toast.error(`${file.name} 不能超过 8 MiB`);
          continue;
        }
        const uploaded = await api.uploadPublicationImage(file);
        setMedia((current) => [...current, uploaded]);
        insert(`\n![${uploaded.originalName}](media://${uploaded.id})\n`);
      }
      toast.success("图片已上传并插入正文");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "图片上传失败");
    } finally {
      setUploading(false);
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (!files.length) return;
    event.preventDefault();
    void uploadFiles(files);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    void uploadFiles(Array.from(event.dataTransfer.files));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error("请填写标题和正文");
      return;
    }
    if (targetScope === "selected" && !selectedUsers.length) {
      toast.error("请至少选择一位目标用户");
      return;
    }
    if (targetScope === "major" && !targetMajorGroups.length) {
      toast.error("请至少选择一个要发送的年级专业");
      return;
    }
    if (
      visibility === "timed" &&
      new Date(expiresAt).getTime() <= new Date(startsAt).getTime()
    ) {
      toast.error("结束时间必须晚于开始时间");
      return;
    }
    const referencedMedia = media.filter((item) =>
      content.includes(`media://${item.id}`),
    );
    if (kind === "notification" && referencedMedia.length) {
      toast.error("通知为纯文字，不能包含图片");
      return;
    }

    const input: CreatePublicationInput = {
      kind,
      title: title.trim(),
      contentMarkdown: content.trim(),
      iconTone: tone,
      accentColor: FIXED_ACCENT,
      visibilityMode: visibility,
      reminderMode: isAnnouncement ? reminder : "inbox",
      targetScope,
      targetUserIds:
        targetScope === "selected" ? selectedUsers.map((user) => user.id) : [],
      ...(targetScope === "major"
        ? {
            targetMajorGroups: targetMajorGroups.map(
              ({ cohortYear, majorName }) => ({ cohortYear, majorName }),
            ),
          }
        : {}),
      startsAt: new Date(startsAt).toISOString(),
      ...(visibility === "timed"
        ? { expiresAt: new Date(expiresAt).toISOString() }
        : {}),
    };

    setSubmitting(true);
    try {
      await api.createPublication(input);
      toast.success(isAnnouncement ? "公告已发布" : "通知已发布");
      resetEditor();
      setMode("current");
      setPage(1);
      await publications.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "发布失败");
    } finally {
      setSubmitting(false);
    }
  };

  const resetEditor = () => {
    setTitle("");
    setContent("");
    setMedia([]);
    setSelectedUsers([]);
    setUserQuery("");
    setUserResults([]);
    setTargetMajorGroups([]);
    setMajorQuery("");
    setTargetScope("all");
    setVisibility("permanent");
    setReminder("once");
    setStartsAt(localDateTime(new Date()));
    setExpiresAt(localDateTime(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)));
  };

  const removePublication = async (publication: PublicationSummary) => {
    setActionBusy(true);
    try {
      await api.deletePublication(publication.id);
      toast.success("内容已移入已删除");
      setSelected(null);
      await publications.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "删除失败");
    } finally {
      setActionBusy(false);
    }
  };

  const restorePublication = async (publication: PublicationSummary) => {
    setActionBusy(true);
    try {
      await api.restorePublication(publication.id);
      toast.success("内容已恢复");
      await publications.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "恢复失败");
    } finally {
      setActionBusy(false);
    }
  };

  const fieldClass =
    "w-full rounded-xl border border-sand-light bg-cream/60 px-3.5 py-2.5 text-[13px] outline-none transition focus:border-clay/50 focus:bg-paper";
  const PreviewIcon = iconOf(tone, kind);

  return (
    <div className="space-y-8">
      <SectionHeader
        eyebrow="BROADCAST"
        title="撰写与发布"
        desc="支持 Markdown 正文 · 公告可粘贴或拖入图片"
        className="rise-in"
      />

      <form
        className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_400px]"
        onSubmit={(event) => void submit(event)}
      >
        <Panel className="rise-in rise-in-1 p-6">
          <div className="flex flex-wrap gap-2">
            {(
              [
                {
                  key: "announcement",
                  label: "公告",
                  icon: Megaphone,
                  hint: "支持图片插入",
                },
                {
                  key: "notification",
                  label: "通知",
                  icon: Bell,
                  hint: "轻量文字触达",
                },
              ] as const
            ).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setKind(item.key)}
                className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-[13px] font-medium transition ${
                  kind === item.key
                    ? "border-ink bg-ink text-paper"
                    : "border-sand-light bg-paper text-ink-soft hover:border-sand"
                }`}
              >
                <item.icon size={14} />
                {item.label}
                <span
                  className={`text-[10.5px] ${kind === item.key ? "text-sand/70" : "text-ink-faint"}`}
                >
                  {item.hint}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-5">
            <label className="eyebrow text-[10px] text-ink-faint">
              TITLE · 标题
            </label>
            <input
              value={title}
              maxLength={160}
              onChange={(event) => setTitle(event.target.value)}
              className="mt-2 w-full rounded-xl border border-sand-light bg-cream/60 px-4 py-3 text-[16px] font-medium outline-none transition focus:border-clay/50 focus:bg-paper"
              placeholder="输入标题…"
            />
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between">
              <label className="eyebrow text-[10px] text-ink-faint">
                BODY · 正文（Markdown）
              </label>
              <div className="flex gap-1">
                {[
                  {
                    icon: Bold,
                    label: "加粗",
                    action: () => wrapSelection("**", "加粗文字"),
                  },
                  {
                    icon: List,
                    label: "列表",
                    action: () => insert("\n- 列表项"),
                  },
                  {
                    icon: Code2,
                    label: "代码块",
                    action: wrapCodeBlock,
                  },
                  {
                    icon: Link2,
                    label: "链接",
                    action: () => insert("[链接文字](https://)"),
                  },
                ].map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    title={item.label}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={item.action}
                    className="rounded-lg border border-sand-light bg-paper p-2 text-ink-soft transition hover:border-sand hover:text-clay-deep active:scale-95"
                  >
                    <item.icon size={13.5} />
                  </button>
                ))}
                {isAnnouncement && (
                  <label
                    title="插入图片"
                    className="cursor-pointer rounded-lg border border-sand-light bg-paper p-2 text-ink-soft transition hover:border-sand hover:text-clay-deep active:scale-95"
                  >
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      multiple
                      className="sr-only"
                      onChange={(event) =>
                        void uploadFiles(Array.from(event.target.files || []))
                      }
                    />
                    <ImagePlus size={13.5} />
                  </label>
                )}
              </div>
            </div>
            <div
              className="mt-2"
              onDragOver={(event) => event.preventDefault()}
              onDrop={onDrop}
            >
              <textarea
                ref={bodyRef}
                value={content}
                maxLength={100000}
                onChange={(event) => setContent(event.target.value)}
                onPaste={onPaste}
                rows={9}
                className={`${fieldClass} resize-y font-mono text-[12.5px] leading-relaxed`}
                placeholder={
                  isAnnouncement
                    ? "支持 Markdown；可将图片粘贴或拖到这里"
                    : "支持 Markdown，通知保持纯文字"
                }
              />
            </div>
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <div>
              <label className="eyebrow text-[10px] text-ink-faint">
                ICON · 选择图标
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {ICONS.map((item) => {
                  const ToneIcon =
                    item.value === "default" && kind === "notification"
                      ? Bell
                      : item.icon;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      title={item.label}
                      aria-label={`选择${item.label}图标`}
                      onClick={() => setTone(item.value)}
                      className={`flex h-10 w-10 items-center justify-center rounded-xl border transition active:scale-90 ${
                        tone === item.value
                          ? "border-clay bg-clay-soft text-clay-deep"
                          : "border-sand-light bg-paper text-ink-faint hover:border-sand"
                      }`}
                    >
                      <ToneIcon size={16} strokeWidth={1.8} />
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="eyebrow text-[10px] text-ink-faint">
                VISIBILITY · 可见期限
              </label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-14 shrink-0 text-[12px] text-ink-soft">
                    开始于
                  </span>
                  <input
                    type="datetime-local"
                    value={startsAt}
                    onChange={(event) => setStartsAt(event.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-14 shrink-0 text-[12px] text-ink-soft">
                    结束于
                  </span>
                  {visibility === "permanent" ? (
                    <div className="flex h-[42px] flex-1 items-center rounded-xl border border-dashed border-sand bg-cream/50 px-3.5 text-[12.5px] text-ink-faint">
                      永久可见
                    </div>
                  ) : (
                    <input
                      type="datetime-local"
                      value={expiresAt}
                      min={startsAt}
                      onChange={(event) => setExpiresAt(event.target.value)}
                      className={fieldClass}
                    />
                  )}
                </div>
                <label className="flex cursor-pointer items-center gap-2 pt-0.5 text-[12.5px] text-ink-soft">
                  <button
                    type="button"
                    aria-label="永久可见"
                    onClick={() =>
                      setVisibility((current) =>
                        current === "permanent" ? "timed" : "permanent",
                      )
                    }
                    className={`relative h-5 w-9 rounded-full transition ${visibility === "permanent" ? "bg-clay" : "bg-sand"}`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-paper shadow transition-all ${visibility === "permanent" ? "left-[18px]" : "left-0.5"}`}
                    />
                  </button>
                  永久可见（不设定结束期限）
                </label>
              </div>
            </div>
          </div>

          <div
            className={`mt-5 grid gap-5 ${isAnnouncement ? "sm:grid-cols-2" : ""}`}
          >
            {kind === "announcement" && (
              <div>
                <label className="eyebrow text-[10px] text-ink-faint">
                  REMINDER · 弹出方式
                </label>
                <div className="mt-2">
                  <SlidingPillSelector
                    label="弹出方式"
                    value={reminder}
                    onChange={setReminder}
                    options={[
                      { value: "once", label: "仅首次" },
                      { value: "inbox", label: "静默" },
                      { value: "always", label: "每次" },
                    ]}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="eyebrow text-[10px] text-ink-faint">
                AUDIENCE · 发送对象
              </label>
              <div className="mt-2">
                <SlidingPillSelector
                  label="发送对象"
                  value={targetScope}
                  onChange={setTargetScope}
                  options={[
                    { value: "all", label: "全体用户", icon: UsersIcon },
                    {
                      value: "selected",
                      label: "指定用户",
                      icon: UserCheck,
                    },
                    {
                      value: "major",
                      label: "年级专业",
                      icon: GraduationCap,
                    },
                  ]}
                />
              </div>
            </div>
          </div>

          {targetScope === "selected" && (
            <div className="mt-3 rounded-xl border border-dashed border-sand bg-cream/50 p-3">
              <div className="flex items-center gap-2 rounded-lg border border-sand-light bg-paper px-3 py-2">
                <Search size={13} className="shrink-0 text-ink-faint" />
                <input
                  value={userQuery}
                  onChange={(event) => setUserQuery(event.target.value)}
                  placeholder="搜索姓名或学号…"
                  className="w-full bg-transparent text-[12.5px] outline-none placeholder:text-ink-faint/70 focus-visible:outline-none focus-visible:ring-0"
                />
                {selectedUsers.length > 0 && (
                  <span className="shrink-0 rounded-full bg-clay-soft px-2 py-0.5 text-[11px] font-medium text-clay-deep">
                    已选 {selectedUsers.length} 人
                  </span>
                )}
              </div>
              <div className="scrollbar-thin mt-2.5 flex max-h-36 flex-wrap content-start gap-2 overflow-y-auto">
                {!userQuery.trim() ? (
                  <p className="w-full py-3 text-center text-[12px] text-ink-faint">
                    输入姓名或学号搜索用户
                  </p>
                ) : usersLoading ? (
                  <p className="w-full py-3 text-center text-[12px] text-ink-faint">
                    正在搜索…
                  </p>
                ) : userResults.length ? (
                  userResults.map((user) => {
                    const selectedUser = selectedUsers.some(
                      (item) => item.id === user.id,
                    );
                    return (
                      <button
                        type="button"
                        key={user.id}
                        onClick={() =>
                          setSelectedUsers((current) =>
                            selectedUser
                              ? current.filter((item) => item.id !== user.id)
                              : [...current, user],
                          )
                        }
                        className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition active:scale-95 ${
                          selectedUser
                            ? "border-clay bg-clay-soft text-clay-deep"
                            : "border-sand-light bg-paper text-ink-soft hover:border-sand"
                        }`}
                      >
                        {selectedUser && "✓ "}
                        {user.displayName} · {user.account.slice(-3)}
                      </button>
                    );
                  })
                ) : (
                  <p className="w-full py-3 text-center text-[12px] text-ink-faint">
                    没有匹配用户
                  </p>
                )}
              </div>
            </div>
          )}

          {targetScope === "major" && (
            <div className="mt-3 rounded-xl border border-dashed border-sand bg-cream/50 p-3">
              <div className="publication-major-field flex items-center gap-2 rounded-lg border border-sand-light bg-paper px-3 py-2">
                <Search size={13} className="shrink-0 text-ink-faint" />
                <input
                  value={majorQuery}
                  onChange={(event) => setMajorQuery(event.target.value)}
                  disabled={majors.loading && !majors.data}
                  aria-label="搜索目标年级专业"
                  placeholder={
                    majors.loading && !majors.data
                      ? "正在读取年级专业…"
                      : "搜索年级或专业…"
                  }
                  className="min-w-0 flex-1 bg-transparent text-[12.5px] text-ink outline-none placeholder:text-ink-faint/70 disabled:text-ink-faint"
                />
                {targetMajorGroups.length > 0 && (
                  <span className="shrink-0 rounded-full bg-clay-soft px-2 py-0.5 text-[11px] font-medium text-clay-deep">
                    已选 {targetMajorGroups.length} 个
                  </span>
                )}
              </div>
              {targetMajorGroups.length > 0 && (
                <div
                  className="mt-2.5 flex flex-wrap gap-2"
                  aria-label="已选年级专业"
                >
                  {targetMajorGroups.map((major) => (
                    <button
                      type="button"
                      key={cohortMajorKey(major)}
                      aria-label={`取消选择${major.label}`}
                      onClick={() =>
                        setTargetMajorGroups((current) =>
                          current.filter(
                            (item) =>
                              cohortMajorKey(item) !== cohortMajorKey(major),
                          ),
                        )
                      }
                      className="flex items-center gap-1.5 rounded-full border border-clay bg-clay-soft px-3 py-1.5 text-[12px] font-medium text-clay-deep transition-[border-color,background-color,color,transform] duration-150 hover:bg-peach active:scale-[0.97] motion-reduce:transform-none motion-reduce:transition-none"
                    >
                      <span>{major.label}</span>
                      <span className="text-[10.5px] font-normal text-clay-deep/70">
                        {major.userCount} 人
                      </span>
                      <X size={12} />
                    </button>
                  ))}
                </div>
              )}
              <div className="scrollbar-thin mt-2.5 flex max-h-44 flex-wrap content-start gap-2 overflow-y-auto">
                {majors.loading && !majors.data ? (
                  <p className="w-full py-3 text-center text-[12px] text-ink-faint">
                    正在读取年级专业…
                  </p>
                ) : filteredMajors.length ? (
                  filteredMajors.map((major) => {
                    const selectedMajor = targetMajorGroups.some(
                      (item) => cohortMajorKey(item) === cohortMajorKey(major),
                    );
                    return (
                      <button
                        type="button"
                        key={cohortMajorKey(major)}
                        aria-pressed={selectedMajor}
                        onClick={() =>
                          setTargetMajorGroups((current) =>
                            selectedMajor
                              ? current.filter(
                                  (item) =>
                                    cohortMajorKey(item) !==
                                    cohortMajorKey(major),
                                )
                              : [...current, major],
                          )
                        }
                        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium transition-[border-color,background-color,color,transform] duration-150 active:scale-[0.97] motion-reduce:transform-none motion-reduce:transition-none ${
                          selectedMajor
                            ? "border-clay bg-clay-soft text-clay-deep"
                            : "border-sand-light bg-paper text-ink-soft hover:border-sand hover:text-ink"
                        }`}
                      >
                        {selectedMajor && <CircleCheck size={12} />}
                        <span>{major.label}</span>
                        <span className="text-[10.5px] font-normal opacity-65">
                          {major.userCount} 人
                        </span>
                      </button>
                    );
                  })
                ) : (
                  <p className="w-full py-3 text-center text-[12px] text-ink-faint">
                    {majors.data?.length
                      ? "没有匹配年级专业"
                      : "暂无可选年级专业"}
                  </p>
                )}
              </div>
              {majors.error && (
                <div className="mt-2 flex items-center justify-between gap-3 px-1 text-[11.5px] text-clay-deep">
                  <span>年级专业列表读取失败</span>
                  <button
                    type="button"
                    onClick={() => void majors.refresh()}
                    className="font-medium underline decoration-clay/40 underline-offset-2"
                  >
                    重新读取
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="mt-7 flex flex-wrap items-center gap-3 border-t border-sand-light/70 pt-5">
            <button
              type="submit"
              disabled={submitting || uploading}
              className="flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-[13px] font-medium text-paper transition hover:bg-clay-deep active:scale-95 disabled:pointer-events-none disabled:opacity-45"
            >
              {submitting ? (
                <RefreshCw size={13.5} className="animate-spin" />
              ) : (
                <Send size={13.5} />
              )}
              {uploading
                ? "正在上传图片"
                : submitting
                  ? "正在发布"
                  : "立即发布"}
            </button>
            <button
              type="button"
              onClick={resetEditor}
              className="rounded-full border border-sand bg-paper px-6 py-2.5 text-[13px] font-medium text-ink-soft transition hover:text-ink active:scale-95"
            >
              清空
            </button>
          </div>
        </Panel>

        <div className="rise-in rise-in-2">
          <div className="sticky top-24">
            <div className="mb-3 flex items-center gap-2">
              <Eye size={14} className="text-ink-faint" />
              <p className="eyebrow text-[10px] text-ink-faint">
                LIVE PREVIEW · 用户视角预览
              </p>
            </div>
            <div className="overflow-hidden rounded-3xl border border-sand-light bg-paper shadow-[0_20px_50px_-20px_rgba(46,42,37,0.25)]">
              <div className="bg-ink px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-clay text-paper">
                    <PreviewIcon size={16} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-sand/70">
                      {isAnnouncement ? "ANNOUNCEMENT" : "NOTICE"}
                    </p>
                    <p className="text-[11px] text-sand">{previewAudience}</p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-5">
                <h3 className="font-display text-[19px] font-medium leading-snug tracking-tight">
                  {title || "未命名标题"}
                </h3>
                <p className="mt-1.5 text-[11.5px] text-ink-faint">
                  {startsAt.replace("T", " ")} 起可见 ·{" "}
                  {visibility === "permanent"
                    ? "永久"
                    : `${expiresAt.replace("T", " ")} 止`}
                </p>
                <div
                  className="markdown-body mt-4 border-t border-sand-light/70 pt-4"
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(
                      content || "正文将在这里显示。",
                      previewMedia,
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </form>

      <section className="rise-in rise-in-3">
        <SectionHeader
          eyebrow="MANAGE"
          title="内容管理"
          desc={
            publications.data
              ? `共 ${publications.data.pagination.total} 条${mode === "current" ? "当前" : "已删除"}内容`
              : undefined
          }
          action={
            <div className="flex rounded-full border border-sand-light bg-paper p-1">
              {(
                [
                  { key: "current", label: "已发布" },
                  { key: "deleted", label: "已删除" },
                ] as const
              ).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setMode(item.key);
                    setPage(1);
                  }}
                  className={`rounded-full px-4 py-1.5 text-[12.5px] font-medium transition ${
                    mode === item.key
                      ? "bg-ink text-paper"
                      : "text-ink-soft hover:text-ink"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          }
        />

        <form
          className="mt-4 flex max-w-sm items-center gap-2 rounded-full border border-sand-light bg-paper px-4 py-2 text-[12.5px]"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setQuery(draftQuery.trim());
          }}
        >
          <Search size={13} className="text-ink-faint" />
          <input
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            placeholder="搜索标题或正文…"
            className="w-full bg-transparent outline-none placeholder:text-ink-faint/70"
          />
        </form>

        {publications.error && (
          <div className="mt-4">
            <ErrorState
              error={publications.error}
              onRetry={() => void publications.refresh()}
            />
          </div>
        )}
        <div className="mt-5 grid gap-3">
          {publications.loading && !publications.data ? (
            <Panel>
              <LoadingState rows={5} />
            </Panel>
          ) : publications.data?.items.length ? (
            <>
              {publications.data.items.map((publication) => {
                const PublicationIcon = iconOf(
                  publication.iconTone,
                  publication.kind,
                );
                return (
                  <Panel
                    key={publication.id}
                    className="flex flex-wrap items-center gap-4 px-5 py-4 transition hover:border-sand"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-peach text-clay-deep">
                      <PublicationIcon size={18} strokeWidth={1.8} />
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelected(publication)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-[14.5px] font-medium text-ink">
                          {publication.title}
                        </p>
                        <Tag
                          tone={
                            publication.kind === "announcement"
                              ? "clay"
                              : "neutral"
                          }
                        >
                          {publicationTypeLabel(publication)}
                        </Tag>
                        <PublicationStatus publication={publication} />
                      </div>
                      <p className="mt-1 truncate text-[12.5px] text-ink-soft">
                        {plainExcerpt(publication.contentMarkdown)}
                      </p>
                      <p className="mt-1 text-[11px] text-ink-faint">
                        {publicationAudienceLabel(publication)} ·{" "}
                        {formatDateTime(publication.startsAt, true)} 起 ·{" "}
                        {publication.visibilityMode === "permanent"
                          ? "永久可见"
                          : `${formatDateTime(publication.expiresAt, true)} 止`}
                      </p>
                    </button>
                    <div className="w-36 shrink-0">
                      <div className="flex items-baseline justify-between text-[11px] text-ink-faint">
                        <span>已读进度</span>
                        <strong className="font-serif-num text-[14px] text-ink">
                          {publication.readPercent}%
                        </strong>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-cream-deep">
                        <span
                          className="block h-full rounded-full bg-gradient-to-r from-sand to-clay"
                          style={{ width: `${publication.readPercent}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        title="查看"
                        onClick={() => setSelected(publication)}
                        className="rounded-lg border border-sand-light p-2 text-ink-soft transition hover:border-sand hover:text-ink"
                      >
                        <Eye size={14} />
                      </button>
                      {publication.deletedAt ? (
                        <button
                          type="button"
                          title="恢复"
                          disabled={
                            actionBusy ||
                            publication.deletedReason === "expired"
                          }
                          onClick={() => void restorePublication(publication)}
                          className="rounded-lg border border-sand-light p-2 text-ink-soft transition hover:border-sand hover:text-sage disabled:opacity-35"
                        >
                          <RefreshCw size={14} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          title="删除"
                          disabled={actionBusy}
                          onClick={() => void removePublication(publication)}
                          className="rounded-lg border border-sand-light p-2 text-ink-soft transition hover:border-[#E8C9B8] hover:text-destructive disabled:opacity-35"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </Panel>
                );
              })}
              <Panel>
                <Pagination
                  page={publications.data.pagination.page}
                  pages={publications.data.pagination.totalPages}
                  total={publications.data.pagination.total}
                  onChange={setPage}
                  className="px-4 py-3"
                />
              </Panel>
            </>
          ) : (
            <Panel>
              <EmptyState
                title={mode === "current" ? "还没有发布内容" : "没有已删除内容"}
                description={
                  mode === "current"
                    ? "在上方完成第一条公告或通知。"
                    : "手动删除或到期软删除的内容会保留在这里。"
                }
              />
            </Panel>
          )}
        </div>
      </section>

      <PublicationDetailDrawer
        summary={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function PublicationStatus({
  publication,
}: {
  publication: PublicationSummary;
}) {
  if (publication.status === "deleted") {
    return (
      <Tag tone="danger">
        {publication.deletedReason === "expired" ? "已到期" : "已删除"}
      </Tag>
    );
  }
  if (publication.status === "scheduled") return <Tag>待发布</Tag>;
  return <Tag tone="sage">发布中</Tag>;
}

function PublicationDetailDrawer({
  summary,
  onClose,
}: {
  summary: PublicationSummary | null;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<PublicationDetail | null>(null);
  const [recipients, setRecipients] = useState<Awaited<
    ReturnType<typeof api.publicationRecipients>
  > | null>(null);
  const [recipientPage, setRecipientPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => setRecipientPage(1), [summary?.id]);
  useEffect(() => {
    if (!summary) {
      setDetail(null);
      setRecipients(null);
      return;
    }
    setLoading(true);
    void Promise.all([
      api.publication(summary.id),
      api.publicationRecipients(summary.id, {
        page: recipientPage,
        pageSize: 30,
      }),
    ])
      .then(([nextDetail, nextRecipients]) => {
        setDetail(nextDetail);
        setRecipients(nextRecipients);
      })
      .catch((error) =>
        toast.error(error instanceof Error ? error.message : "详情读取失败"),
      )
      .finally(() => setLoading(false));
  }, [summary, recipientPage]);

  return createPortal(
    <div
      className={`drawer-layer ${summary ? "drawer-layer--open" : ""}`}
      aria-hidden={!summary}
    >
      <button
        className="drawer-backdrop"
        aria-label="关闭详情"
        onClick={onClose}
      />
      <aside
        className="drawer publication-detail-drawer"
        role="dialog"
        aria-modal="true"
      >
        <div className="drawer-header">
          <div>
            <span className="eyebrow text-[9px] text-clay">READING STATUS</span>
            <h2>{summary?.title || "内容详情"}</h2>
            <p>{summary ? publicationTypeLabel(summary) : ""}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">
            <X size={18} />
          </button>
        </div>
        <div className="drawer-content">
          {loading && !detail ? (
            <LoadingState rows={8} />
          ) : detail ? (
            <>
              <section className="read-overview">
                <div>
                  <strong>{detail.readPercent}%</strong>
                  <span>阅读进度</span>
                </div>
                <div className="progress-track progress-track--large">
                  <span style={{ width: `${detail.readPercent}%` }} />
                </div>
                <small>
                  {detail.readCount} 人已读 · {detail.unreadCount} 人未读
                </small>
              </section>
              <section className="drawer-section">
                <div className="drawer-section-title">
                  <h3>内容预览</h3>
                </div>
                <article className="detail-publication-preview">
                  <h3>{detail.title}</h3>
                  <div
                    className="markdown-body"
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdown(
                        detail.contentMarkdown,
                        detail.media,
                      ),
                    }}
                  />
                </article>
              </section>
              <section className="drawer-section">
                <div className="drawer-section-title">
                  <h3>用户状态</h3>
                  <span>{recipients?.pagination.total || 0}</span>
                </div>
                <div className="recipient-list">
                  {recipients?.items.map((recipient) => (
                    <div key={recipient.userId}>
                      <span className="person-avatar">
                        {recipient.displayName.slice(0, 1)}
                      </span>
                      <span>
                        <strong>{recipient.displayName}</strong>
                        <small>{recipient.account}</small>
                      </span>
                      <span
                        className={
                          recipient.readAt
                            ? "recipient-read"
                            : "recipient-unread"
                        }
                      >
                        {recipient.readAt
                          ? formatDateTime(recipient.readAt, true)
                          : "未读"}
                      </span>
                    </div>
                  ))}
                </div>
                {recipients && recipients.pagination.totalPages > 1 && (
                  <Pagination
                    page={recipients.pagination.page}
                    pages={recipients.pagination.totalPages}
                    total={recipients.pagination.total}
                    onChange={setRecipientPage}
                    className="mt-3"
                  />
                )}
              </section>
            </>
          ) : null}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

function plainExcerpt(markdown: string) {
  return (
    markdown
      .replace(/!\[[^\]]*\]\([^)]+\)/g, "[图片]")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/[#*_>`~-]/g, "")
      .replace(/\s+/g, " ")
      .trim() || "暂无正文预览"
  );
}

function localDateTime(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
